import {useCurrentUserActionsCount} from '@/modules/patients-therapies/api'
import {getTherapySequenceStatusOptions} from '@/modules/patients-therapies/constants'
import {PatientsTherapiesFiltersSchema} from '@/modules/patients-therapies/schemas'
import {mapSectionsToSelectItems} from '@/modules/sections/functions'
import {useGetSettings} from '@/modules/settings/api'
import {mapWardsToSelectItems} from '@/modules/wards/functions'
import {FilterButton} from '@cypro/ui/components/ui/filter-button'
import {withForm} from '@cypro/ui/integrations/form'
import {isDefined, parseDateFilterText} from '@cypro/ui/utils'
import {Badge, ButtonGroup, IconButton} from '@chakra-ui/react'
import {queries} from '@cypro/engine/queries'
import {useQuery} from '@rocicorp/zero/react'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {LuCircleX, LuLogs} from 'react-icons/lu'

export const PatientsTherapiesFilters = withForm({
  defaultValues: {} as PatientsTherapiesFiltersSchema,
  render: function Render({form}) {
    const {t} = useTranslate('common')
    const {t: tActivity} = useTranslate('activity')

    const [wards] = useQuery(queries.locations.getWardsWithPatientEncounters())
    const [sections] = useQuery(queries.locations.getSectionsWithPatientEncounters())
    const {settings} = useGetSettings()
    const {actionsCount} = useCurrentUserActionsCount({therapyDate: form.state.values.therapyDate?.[0]})

    const wardsSelectItems = mapWardsToSelectItems(wards)
    const sectionsSelectItems = mapSectionsToSelectItems(sections)

    const statusSelectItems = getTherapySequenceStatusOptions(settings).map(status => ({
      label: tActivity(`${status}.title`),
      value: status,
    }))

    const hasValues = useStore(form.store, state =>
      Boolean(
        state.values?.search ||
        state.values?.wards?.length ||
        state.values?.sections?.length ||
        state.values?.therapyDate?.length ||
        state.values?.status?.length ||
        state.values?.onlyCurrentUserActions
      )
    )

    function onClearFilters() {
      form.setFieldValue('search', '')
      form.setFieldValue('wards', [])
      form.setFieldValue('sections', [])
      form.setFieldValue('therapyDate', [])
      form.setFieldValue('status', [])
      form.setFieldValue('onlyCurrentUserActions', false)
    }

    return (
      <ButtonGroup size="sm" gapX="2">
        <form.AppField name="onlyCurrentUserActions">
          {field => (
            <FilterButton
              isApplied={Boolean(field.state.value)}
              hideIcon
              onClick={() => field.handleChange(!field.state.value)}
              role="checkbox"
              aria-checked={Boolean(field.state.value)}
            >
              <LuLogs />
              {tActivity('my_active_actions')}
              {isDefined(actionsCount) && (
                <Badge size="sm" variant="outline">
                  {actionsCount}
                </Badge>
              )}
            </FilterButton>
          )}
        </form.AppField>

        <form.AppField name="therapyDate">
          {field => (
            <field.DatePicker label={t('therapy_date')} hideLabel valueAs="date-string">
              <FilterButton isApplied={Boolean(field.state.value?.length)}>
                {!field.state.value?.length && t('therapy_date')}

                {Boolean(field.state.value?.length) &&
                  field.state.value &&
                  parseDateFilterText(field.state.value)
                    .map(item => t(item))
                    .join(' - ')}
              </FilterButton>
            </field.DatePicker>
          )}
        </form.AppField>

        <form.AppField name="status">
          {field => (
            <field.Select
              items={statusSelectItems}
              label={t('status')}
              hideLabel
              multiple
              positioning={{strategy: 'fixed'}}
            >
              <FilterButton isApplied={Boolean(field.state.value?.length)}>{t('status')}</FilterButton>
            </field.Select>
          )}
        </form.AppField>

        <form.AppField name="sections">
          {field => (
            <field.Select
              items={sectionsSelectItems}
              label={t('sections')}
              hideLabel
              multiple
              positioning={{strategy: 'fixed'}}
            >
              <FilterButton isApplied={Boolean(field.state.value?.length)}>{t('sections')}</FilterButton>
            </field.Select>
          )}
        </form.AppField>

        <form.AppField name="wards">
          {field => (
            <field.Select
              items={wardsSelectItems}
              label={t('wards')}
              hideLabel
              multiple
              positioning={{strategy: 'fixed'}}
            >
              <FilterButton isApplied={Boolean(field.state.value?.length)}>{t('wards')}</FilterButton>
            </field.Select>
          )}
        </form.AppField>

        {hasValues && (
          <IconButton variant="ghost" title={t('clear_filters')} onClick={onClearFilters}>
            <LuCircleX />
          </IconButton>
        )}
      </ButtonGroup>
    )
  },
})
